import { useEffect, useState, type FormEvent } from 'react';
import {
  User as UserIcon, SlidersHorizontal, Bell, KeyRound, Lock, LogOut,
  Eye, EyeOff, Copy, Check, Plus, Trash2, X, AlertCircle, Loader2,
} from 'lucide-react';
import type { Theme } from '../lib/theme';
import { API_BASE } from '../lib/api';
import {
  listApiKeys, createApiKey, revokeApiKey,
  listSecrets, putSecret, deleteSecret,
  type AccountApiKey,
} from '../lib/account';

interface AccountUser {
  id: string;
  username?: string | null;
  name?: string | null;
  email?: string | null;
  role: string;
  createdAt?: string | null;
}

interface AccountModalProps {
  open: boolean;
  onClose: () => void;
  user: AccountUser;
  theme: Theme;
  onSetTheme: (t: Theme) => void;
  onSignOut: () => void;
}

type Section = 'profile' | 'general' | 'notifications' | 'api-keys' | 'secrets';

const NAV: { id: Section; label: string; icon: typeof UserIcon }[] = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
  { id: 'general', label: 'General', icon: SlidersHorizontal },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'api-keys', label: 'API Keys', icon: KeyRound },
  { id: 'secrets', label: 'Secrets', icon: Lock },
];

export function AccountModal({ open, onClose, user, theme, onSetTheme, onSignOut }: AccountModalProps) {
  const [section, setSection] = useState<Section>('profile');

  // Esc-to-close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset to the first section each time the modal opens.
  useEffect(() => { if (open) setSection('profile'); }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      role="dialog"
      aria-modal="true"
      aria-label="Account settings"
      onClick={onClose}
    >
      <div
        className="w-[640px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] flex flex-col rounded-xl shadow-2xl border animate-scaleUp"
        style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-5 h-14 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
          <h2 className="text-sm font-semibold">Account</h2>
          <button
            onClick={onClose}
            className="ml-auto p-1.5 rounded-md transition"
            style={{ color: 'var(--ink-secondary)' }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 2-column body */}
        <div className="flex flex-col md:flex-row min-h-0 flex-1">
          {/* Left rail */}
          <nav className="md:w-48 shrink-0 md:border-r border-b md:border-b-0 p-2 flex md:flex-col gap-0.5 overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
            {NAV.map((item) => {
              const active = section === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setSection(item.id)}
                  className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition whitespace-nowrap"
                  style={{
                    color: active ? 'var(--accent)' : 'var(--ink)',
                    background: active ? 'var(--accent-bg)' : 'transparent',
                  }}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <div className="hidden md:block h-px my-2" style={{ background: 'var(--border)' }} />
            <button
              onClick={onSignOut}
              className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-sm transition whitespace-nowrap"
              style={{ color: 'var(--ink-secondary)' }}
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              <span>Log out</span>
            </button>
          </nav>

          {/* Right pane */}
          <div className="flex-1 overflow-y-auto p-6">
            {section === 'profile' && <ProfileSection user={user} />}
            {section === 'general' && <GeneralSection theme={theme} onSetTheme={onSetTheme} />}
            {section === 'notifications' && <NotificationsSection />}
            {section === 'api-keys' && <ApiKeysSection />}
            {section === 'secrets' && <SecretsSection />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- shared bits ----------

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-5">
      <h3 className="text-base font-semibold">{title}</h3>
      {subtitle && <p className="text-xs mt-1" style={{ color: 'var(--ink-secondary)' }}>{subtitle}</p>}
    </div>
  );
}

function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 p-3 rounded-md mb-4 text-xs" style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}>
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
  );
}

function Spinner({ className = '' }: { className?: string }) {
  return <Loader2 className={`w-4 h-4 animate-spin ${className}`} style={{ color: 'var(--ink-secondary)' }} />;
}

// ---------- MCP client setup (copy blocks) ----------

const MCP_URL = `${API_BASE}/api/mcp`;

/** Build the ready-to-paste MCP config for each supported client. `token` is
 *  either the just-created key (banner) or a placeholder (persistent panel). */
function mcpClientConfigs(token: string): { id: string; label: string; file: string; body: string }[] {
  const auth = `Bearer ${token}`;
  const jsonHttp = JSON.stringify({
    mcpServers: { nim: { type: 'http', url: MCP_URL, headers: { Authorization: auth } } },
  }, null, 2);
  return [
    { id: 'claude-code', label: 'Claude Code', file: '.mcp.json (project root)', body: jsonHttp },
    { id: 'desktop', label: 'Claude Desktop', file: 'claude_desktop_config.json', body: jsonHttp },
    {
      id: 'cursor', label: 'Cursor', file: '.cursor/mcp.json',
      body: JSON.stringify({ mcpServers: { nim: { url: MCP_URL, headers: { Authorization: auth } } } }, null, 2),
    },
    {
      id: 'codex', label: 'Codex', file: '~/.codex/config.toml',
      // ponytail: token + url are inlined into a double-quoted TOML/argv string.
      // Safe today only because generateApiKey emits base64url (A-Za-z0-9-_, no
      // " \ or whitespace) and MCP_URL is an https origin. If tokens ever allow
      // other chars, escape them here (TOML double-quote + argv rules).
      body: [
        '# Codex has no native Bearer-header HTTP MCP yet — bridge via mcp-remote (needs Node/npx).',
        '[mcp_servers.nim]',
        'command = "npx"',
        `args = ["-y", "mcp-remote", "${MCP_URL}", "--header", "Authorization:${auth}"]`,
      ].join('\n'),
    },
  ];
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };
  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition shrink-0"
      style={{ color: copied ? 'var(--accent)' : 'var(--ink-secondary)', border: '1px solid var(--border)' }}
      aria-label={label}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      <span>{copied ? 'Copied' : label}</span>
    </button>
  );
}

/** Tabbed copy blocks for the four MCP clients. */
function McpSetup({ token }: { token: string }) {
  const clients = mcpClientConfigs(token);
  const [tab, setTab] = useState(clients[0].id);
  const active = clients.find((c) => c.id === tab) ?? clients[0];
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-2">
        {clients.map((c) => {
          const on = c.id === tab;
          return (
            <button
              key={c.id}
              onClick={() => setTab(c.id)}
              className="px-2 py-1 rounded text-xs transition"
              style={{
                background: on ? 'var(--accent-bg)' : 'transparent',
                color: on ? 'var(--accent)' : 'var(--ink-secondary)',
                border: '1px solid var(--border)',
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <code className="text-xs font-mono" style={{ color: 'var(--ink-secondary)' }}>{active.file}</code>
        <CopyButton text={active.body} />
      </div>
      <pre
        className="text-xs font-mono p-2.5 rounded-md overflow-x-auto"
        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
      >
        {active.body}
      </pre>
    </div>
  );
}

// ---------- Profile ----------

function ProfileSection({ user }: { user: AccountUser }) {
  const displayName = user.name || user.username || user.email || 'user';
  const initial = displayName.charAt(0).toUpperCase();
  return (
    <div>
      <SectionHeader title="Profile" subtitle="Your account identity." />
      <div className="flex items-center gap-4 mb-6">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold"
          style={{ background: 'var(--accent-bg)', color: 'var(--accent)' }}
        >
          {initial}
        </div>
        <div>
          <div className="text-sm font-medium">{displayName}</div>
          <div className="text-xs" style={{ color: 'var(--ink-secondary)' }}>{user.role}</div>
        </div>
      </div>
      <dl className="space-y-3 text-sm">
        <Row label="Username" value={user.username} />
        <Row label="Email" value={user.email} />
        <Row label="Role" value={user.role} />
        {user.createdAt && <Row label="Member since" value={new Date(user.createdAt).toLocaleDateString()} />}
      </dl>
      <p className="text-xs mt-6" style={{ color: 'var(--ink-secondary)' }}>
        Contact an admin to change your username, email, or role.
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
      <dt style={{ color: 'var(--ink-secondary)' }}>{label}</dt>
      <dd className="font-medium text-right truncate">{value || '—'}</dd>
    </div>
  );
}

// ---------- General ----------

function GeneralSection({ theme, onSetTheme }: { theme: Theme; onSetTheme: (t: Theme) => void }) {
  const options: { id: Theme; label: string }[] = [
    { id: 'light', label: 'Light' },
    { id: 'dark', label: 'Dark' },
  ];
  return (
    <div>
      <SectionHeader title="General" subtitle="Appearance and display preferences." />
      <div className="mb-2 text-xs font-medium" style={{ color: 'var(--ink-secondary)' }}>Theme</div>
      <div className="inline-flex p-1 rounded-md border" style={{ borderColor: 'var(--border)' }}>
        {options.map((o) => {
          const active = theme === o.id;
          return (
            <button
              key={o.id}
              onClick={() => onSetTheme(o.id)}
              className="px-3 py-1 rounded text-sm transition"
              style={{
                background: active ? 'var(--accent-bg)' : 'transparent',
                color: active ? 'var(--accent)' : 'var(--ink-secondary)',
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Notifications (local prefs) ----------

const NOTIF_KEY = 'nox-notif-prefs';
const NOTIF_PREFS = [
  { id: 'reviewComplete', label: 'A review completes on my PR' },
  { id: 'criticalFinding', label: 'A critical security finding is posted' },
  { id: 'weeklyDigest', label: 'Weekly digest of review activity' },
];

function NotificationsSection() {
  const [prefs, setPrefs] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(NOTIF_KEY) || '{}'); } catch { return {}; }
  });
  const toggle = (id: string) => {
    const next = { ...prefs, [id]: !prefs[id] };
    setPrefs(next);
    try { localStorage.setItem(NOTIF_KEY, JSON.stringify(next)); } catch { /* storage may be blocked */ }
  };
  return (
    <div>
      <SectionHeader title="Notifications" subtitle="Notify me when…" />
      <ul className="space-y-1">
        {NOTIF_PREFS.map((p) => (
          <li key={p.id}>
            <label className="flex items-center gap-3 px-2 py-2 rounded-md cursor-pointer text-sm" style={{ color: 'var(--ink)' }}>
              <input
                type="checkbox"
                checked={!!prefs[p.id]}
                onChange={() => toggle(p.id)}
                className="accent-[var(--accent)]"
              />
              <span>{p.label}</span>
            </label>
          </li>
        ))}
      </ul>
      <p className="text-xs mt-4" style={{ color: 'var(--ink-secondary)' }}>
        Saved to this browser only. Notification delivery is not wired to a backend yet — these preferences do not send anything.
      </p>
    </div>
  );
}

// ---------- API Keys ----------

function ApiKeysSection() {
  const [keys, setKeys] = useState<AccountApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<{ key: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = async () => {
    try { setKeys(await listApiKeys()); setError(null); }
    catch (e) { setError(String(e instanceof Error ? e.message : e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    setError(null);
    try {
      const created = await createApiKey(trimmed);
      setJustCreated({ key: created.key, name: created.name });
      setName('');
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (id: string) => {
    setError(null);
    try { await revokeApiKey(id); await refresh(); }
    catch (e) { setError(String(e instanceof Error ? e.message : e)); }
  };

  const copy = async () => {
    if (!justCreated) return;
    try { await navigator.clipboard.writeText(justCreated.key); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard blocked */ }
  };

  return (
    <div>
      <SectionHeader title="API Keys" subtitle="Tokens for programmatic access (MCP). Store the value now — it is shown once." />

      <ErrorBanner message={error} />

      {justCreated && (
        <div className="mb-4 p-3 rounded-md border" style={{ borderColor: 'var(--accent)', background: 'var(--accent-bg)' }}>
          <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--accent)' }}>
            Created “{justCreated.name}” — copy it now, you won’t see it again.
          </div>
          <div className="flex items-center gap-2 mb-3">
            <code className="flex-1 px-2 py-1.5 rounded text-xs font-mono break-all" style={{ background: 'var(--bg)', color: 'var(--ink)' }}>
              {justCreated.key}
            </code>
            <button onClick={copy} className="p-1.5 rounded transition shrink-0" style={{ color: 'var(--accent)' }} aria-label="Copy key">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-xs font-medium mb-1.5" style={{ color: 'var(--ink-secondary)' }}>
            Add to your AI client (token pre-filled):
          </div>
          <McpSetup token={justCreated.key} />
        </div>
      )}

      <form onSubmit={onCreate} className="flex items-center gap-2 mb-4">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Key name (e.g. laptop, ci)"
          maxLength={64}
          className="flex-1 px-3 py-1.5 rounded-md border text-sm outline-none"
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
        />
        <button
          type="submit"
          disabled={creating || !name.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition disabled:opacity-50"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {creating ? <Spinner /> : <Plus className="w-4 h-4" />}
          <span>Create</span>
        </button>
      </form>

      {loading ? (
        <div className="py-6 flex justify-center"><Spinner /></div>
      ) : keys.length === 0 ? (
        <p className="text-xs py-4" style={{ color: 'var(--ink-secondary)' }}>No active keys.</p>
      ) : (
        <ul className="space-y-1">
          {keys.map((k) => (
            <li key={k.id} className="flex items-center gap-3 px-3 py-2 rounded-md border" style={{ borderColor: 'var(--border)' }}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{k.name}</div>
                <div className="text-xs font-mono" style={{ color: 'var(--ink-secondary)' }}>
                  {k.prefix}… · created {new Date(k.createdAt).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => onRevoke(k.id)}
                className="flex items-center gap-1 px-2 py-1 rounded text-xs transition"
                style={{ color: '#dc2626' }}
                title="Revoke"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Revoke</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="text-xs font-medium mb-1">MCP server</div>
        <p className="text-xs mb-3" style={{ color: 'var(--ink-secondary)' }}>
          Point your AI client at <code className="font-mono">{MCP_URL}</code>. Create a key above and paste it where{' '}
          <code className="font-mono">&lt;TOKEN&gt;</code> appears.
        </p>
        <McpSetup token="<TOKEN>" />
      </div>
    </div>
  );
}

// ---------- Secrets ----------

function SecretsSection() {
  const [secrets, setSecrets] = useState<{ key: string; updatedAt: string }[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const refresh = async () => {
    try {
      const list = await listSecrets();
      setSecrets(list.map((s) => ({ key: s.key, updatedAt: s.updatedAt })));
      setValues(Object.fromEntries(list.map((s) => [s.key, s.value])));
      setError(null);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    const key = newKey.trim().toUpperCase();
    const value = newValue;
    if (!key || !value) return;
    if (!/^[A-Z0-9_]{1,64}$/.test(key)) {
      setError('Key must be 1-64 chars of A-Z, 0-9, or underscore.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await putSecret(key, value);
      setNewKey(''); setNewValue('');
      await refresh();
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (key: string) => {
    setError(null);
    try { await deleteSecret(key); await refresh(); }
    catch (e) { setError(String(e instanceof Error ? e.message : e)); }
  };

  const toggleReveal = (key: string) => {
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div>
      <SectionHeader title="Secrets" subtitle="Encrypted values for this account (API tokens, credentials)." />

      <ErrorBanner message={error} />

      <form onSubmit={onAdd} className="space-y-2 mb-5">
        <input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder="KEY (e.g. OPENAI_API_KEY)"
          className="w-full px-3 py-1.5 rounded-md border text-sm font-mono outline-none"
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
        />
        <input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="Value"
          className="w-full px-3 py-1.5 rounded-md border text-sm outline-none"
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
        />
        <button
          type="submit"
          disabled={saving || !newKey.trim() || !newValue}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition disabled:opacity-50"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {saving ? <Spinner /> : <Plus className="w-4 h-4" />}
          <span>Save secret</span>
        </button>
      </form>

      {loading ? (
        <div className="py-6 flex justify-center"><Spinner /></div>
      ) : secrets.length === 0 ? (
        <p className="text-xs py-4" style={{ color: 'var(--ink-secondary)' }}>No secrets stored.</p>
      ) : (
        <ul className="space-y-1">
          {secrets.map((s) => {
            const show = revealed.has(s.key);
            const val = values[s.key] ?? '';
            return (
              <li key={s.key} className="flex items-center gap-2 px-3 py-2 rounded-md border" style={{ borderColor: 'var(--border)' }}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono font-medium">{s.key}</div>
                  <div className="text-xs font-mono truncate" style={{ color: 'var(--ink-secondary)' }}>
                    {show ? val : '••••••••'}
                  </div>
                </div>
                <button onClick={() => toggleReveal(s.key)} className="p-1.5 rounded transition" style={{ color: 'var(--ink-secondary)' }} aria-label={show ? 'Hide' : 'Reveal'}>
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={() => onDelete(s.key)} className="p-1.5 rounded transition" style={{ color: '#dc2626' }} aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
